import { pdf } from '@react-pdf/renderer';
import { format } from 'date-fns';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { AdyenDropIn } from 'src/components/AdyenPayment/AdyenDropIn';
import { CancelButton } from 'src/components/Buttons';
import { RetailInvoicePDF } from 'src/components/RetailInvoice';
import { useActivePosEntityContext } from 'src/contexts/ActivePosEntityContext';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogDataContextEventsTransformedSelector } from 'src/contexts/CatalogDataContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { Content } from 'src/contexts/ContentContext';
import { ModalContext } from 'src/contexts/ModalContext';
import { vars } from 'src/core/themes';
import { Button, Stack } from 'src/core/ui';
import { useEventItemLoadingDisplay } from 'src/hooks/useEventItemLoadingDisplay';
import { EyeIcon } from 'src/svgs/EyeIcon';
import { ContentId } from 'src/utils/constants/contentId';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import {
  ActionOutboxEntityType,
  EventWithData,
  ListingDetails,
} from 'src/WebApiController';

import { CancellableFormHeader } from '../common/CancellableFormHeader';
import { HeaderContent } from '../common/EventEntityHeader/EventEntityHeader.styled';
import { Summary } from '../common/Summary';
import {
  ModalBody,
  ModalBodyDataContainer,
  ModalBodyHeaderContainer,
  ModalFooter,
} from '../Modal/Modal.styled';
import { RetailSaleBuyerStep } from './components/RetailSaleBuyerStep';
import { RetailSaleCheckoutStep } from './components/RetailSaleCheckoutStep';
import { RetailSaleInvoicePreviewStep } from './components/RetailSaleInvoicePreviewStep';
import * as styles from './RetailSale.css';
import {
  RetailSaleLocalContextProvider,
  useRetailSaleContext,
} from './RetailSaleLocalContext';
import { useRetailSaleStep } from './RetailSaleLocalContext.atoms';
import { getRetailExternalSaleId } from './RetailSaleLocalContext.utils';
import { RetailSaleFormValues } from './types';

export const RetailSale = () => {
  const { posEntity, posEntityDisplayId, posEntityId } =
    useActivePosEntityContext<ListingDetails>();

  const listing = useMemo(() => {
    return posEntity?.entityType === ActionOutboxEntityType.Listing
      ? (posEntity as ListingDetails)
      : undefined;
  }, [posEntity]);

  const { isLoading: entityIsLoading, loadingState } =
    useEventItemLoadingDisplay<ListingDetails>(
      FormatContentId.LoadingListingId,
      FormatContentId.SearchingForListingId,
      FormatContentId.CouldNotFindListingId,
      posEntityDisplayId ?? posEntityId?.toString()
    );

  const methods = useForm<RetailSaleFormValues>({
    defaultValues: {},
  });

  const eventsTransformed = useCatalogDataContextEventsTransformedSelector();
  const event = useMemo(() => {
    return eventsTransformed?.find(
      (e) => e.event?.virtId === listing?.viagVirtId
    );
  }, [eventsTransformed, listing?.viagVirtId]);

  useEffect(() => {
    if (listing) {
      methods.reset({
        externalSaleId: getRetailExternalSaleId(listing.id),
        listingId: listing.id ?? 0,
        currencyCode: listing.currency ?? 'USD',
        saleDate: new Date().toISOString(),
        useShippingAddressOnFile: false,
        billingAddressSameAsShipping: true,
        tags: listing.tags,
        quantitySold: listing.availQty,
        section: listing.seating.section,
        row: listing.seating.row,
        seatFrom: listing.seating.seatFr,
        seatTo: listing.seating.seatTo,
        marketplacePaymentLines: [],
        ticketType: listing.topPriTktType ?? undefined,
      });
    }
  }, [listing, methods]);

  if (!listing && entityIsLoading) {
    return loadingState;
  }

  return (
    <FormProvider {...methods}>
      <RetailSaleLocalContextProvider event={event} listing={listing}>
        <RetailSaleContent event={event} listing={listing} />
      </RetailSaleLocalContextProvider>
    </FormProvider>
  );
};

const RetailSaleContent = ({
  event,
  listing,
}: {
  event?: EventWithData;
  listing?: ListingDetails;
}) => {
  const { closeModal } = useContext(ModalContext);
  const onCancel = useCallback(() => closeModal(true), [closeModal]);

  const { resetStep } = useRetailSaleStep();

  useEffect(() => {
    // Reset the step when retail creation unmounted
    return () => {
      resetStep();
    };
  }, [resetStep]);

  return (
    <>
      <CancellableFormHeader onClose={onCancel}>
        <HeaderContent>
          <Stack direction="row" gap="m" alignItems="center">
            <span className={styles.breadcrumbHeader}>
              <Content id={ContentId.RetailSale} />
            </span>
            <span className={styles.breadcrumbSeparator}>&gt;</span>
            <span className={styles.breadcrumbItem}>
              <Content id={ContentId.Checkout} />
            </span>
          </Stack>
        </HeaderContent>
      </CancellableFormHeader>

      <ModalBody>
        {event?.event && (
          <ModalBodyHeaderContainer>
            <Summary
              event={event.event}
              posEntity={listing}
              entityType={ActionOutboxEntityType.Listing}
            />
          </ModalBodyHeaderContainer>
        )}

        <ModalBodyDataContainer>
          <ModalBodyContent listing={listing} event={event} />
        </ModalBodyDataContainer>
      </ModalBody>

      <ModalFooter>
        <ModalFooterContent onCancel={onCancel} />
      </ModalFooter>
    </>
  );
};

const ModalBodyContent = ({
  listing,
  event,
}: {
  listing?: ListingDetails;
  event?: EventWithData;
}) => {
  const { step } = useRetailSaleStep();
  const { adyenSession, onAdyenPaymentCompleted, onAdyenError } =
    useRetailSaleContext();
  if (adyenSession) {
    return (
      <AdyenDropIn
        sessionId={adyenSession.sessionId}
        sessionData={adyenSession.sessionData}
        clientKey={adyenSession.clientKey}
        environment={adyenSession.environment}
        countryCode={adyenSession.countryCode}
        onPaymentCompleted={onAdyenPaymentCompleted}
        onError={onAdyenError}
      />
    );
  }

  switch (step) {
    case 'detail':
      return <RetailSaleBuyerStep listing={listing} />;
    case 'checkout':
      return <RetailSaleCheckoutStep />;
    case 'invoicePreview':
      return <RetailSaleInvoicePreviewStep listing={listing} event={event} />;
    default:
      return null;
  }
};

const ModalFooterContent = ({ onCancel }: { onCancel: () => void }) => {
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const { step, goToCheckout, goToInvoicePreview } = useRetailSaleStep();
  const {
    disabled,
    adyenSession,
    selectedBuyer,
    onUpsertBuyer,
    onCompleteAsUnpaid,
    onPayAndComplete,
  } = useRetailSaleContext();
  const { loginContext } = useAppContext();
  const { showErrorDialog } = useClientLogContext();
  const { watch } = useFormContext<RetailSaleFormValues>();
  const input = watch();

  const isValidBuyer = useMemo(() => {
    if (
      selectedBuyer == null ||
      selectedBuyer.shippingAddressLine1 == null ||
      selectedBuyer.billingAddressLine1 == null
    ) {
      return (
        !!input.buyerName &&
        !!input.buyerEmail &&
        !!input.buyerPhone &&
        !!input.shippingStreet &&
        !!input.shippingState &&
        !!input.shippingCountry &&
        !!input.shippingZipCode &&
        (input.billingAddressSameAsShipping ||
          (!!input.billingStreet &&
            !!input.billingState &&
            !!input.billingCountry &&
            !!input.billingZipCode))
      );
    }
    return true;
  }, [
    input.billingAddressSameAsShipping,
    input.billingCountry,
    input.billingState,
    input.billingStreet,
    input.billingZipCode,
    input.buyerEmail,
    input.buyerName,
    input.buyerPhone,
    input.shippingCountry,
    input.shippingState,
    input.shippingStreet,
    input.shippingZipCode,
    selectedBuyer,
  ]);

  const canContinueToPayment = useMemo(
    () =>
      isValidBuyer &&
      !!input.saleDate &&
      input.totalNetProceeds != null &&
      input.totalNetProceeds > 0 &&
      !!input.externalSaleId &&
      !!input.ticketType &&
      input.quantitySold != null &&
      input.quantitySold > 0,
    [
      input.externalSaleId,
      input.quantitySold,
      input.saleDate,
      input.ticketType,
      input.totalNetProceeds,
      isValidBuyer,
    ]
  );

  const onMoveToNext = useCallback(async () => {
    const ok = await onUpsertBuyer();
    if (ok) goToCheckout();
  }, [goToCheckout, onUpsertBuyer]);

  const handleDownloadInvoice = useCallback(async () => {
    setIsDownloading(true);
    try {
      const values = input;
      const proceeds = values.totalNetProceeds ?? 0;
      const marketplaceLinesCost =
        values.marketplacePaymentLines?.reduce(
          (sum, item) => sum + (item.amount ?? 0),
          0
        ) ?? 0;
      const totalAmount = proceeds + marketplaceLinesCost;

      const blob = await pdf(
        <RetailInvoicePDF
          brokerName={loginContext?.user?.activeAccount?.accountName ?? ''}
          buyerName={values.buyerName}
          buyerEmail={values.buyerEmail}
          buyerAddress={[
            values.shippingStreet,
            values.shippingCity,
            values.shippingState,
            values.shippingCountry,
          ]
            .filter(Boolean)
            .join(', ')}
          invoiceNumber={values.externalSaleId ?? `${values.listingId}`}
          invoiceDate={values.saleDate ?? new Date().toISOString()}
          currency={values.currencyCode ?? 'USD'}
          paymentTermsDays={values.paymentTermsDays}
          lineItems={[
            ...(proceeds !== 0
              ? [
                  {
                    description: 'Ticket Proceeds',
                    amount: proceeds,
                    costType: 'Proceeds',
                  },
                ]
              : []),
            ...(values.marketplacePaymentLines ?? []).map((line) => ({
              description: line.description ?? '',
              ratePercent: line.ratePercent ?? undefined,
              amount: line.amount ?? 0,
              costType: (line.amount ?? 0) < 0 ? 'Credit' : 'Charge',
            })),
          ]}
          status={values.paymentReceived === true ? 'Paid' : 'Outstanding'}
          totalAmount={totalAmount}
          balanceDue={totalAmount}
          paymentsMade={0}
          brokerNotes={values.privateNotes}
        />
      ).toBlob();

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `RetailInvoice_${values.listingId}_${format(new Date(), 'yyyy-MM-dd')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      showErrorDialog('RetailSale.handleDownloadInvoice', error as Error, {
        trackErrorData: { listingId: input.listingId },
      });
    } finally {
      setIsDownloading(false);
    }
  }, [input, loginContext, showErrorDialog]);

  // When adyen drop in session present, we are actioning based on adyen callbacks
  if (adyenSession) {
    return null;
  }

  if (step === 'detail') {
    return (
      <Stack direction="row" gap="m" justifyContent="end" width="full">
        <CancelButton type="button" onClick={onCancel} />
        <Button
          type="button"
          onClick={onMoveToNext}
          disabled={!canContinueToPayment}
        >
          <Content id={ContentId.ContinueToPayment} />
        </Button>
      </Stack>
    );
  }

  if (step === 'checkout') {
    return (
      <Stack direction="row" justifyContent="spaceBetween" width="full">
        <Stack direction="row">
          <CancelButton type="button" disabled={disabled} onClick={onCancel} />
        </Stack>
        <Stack direction="row" gap="m">
          <Button type="button" variant="outline" onClick={goToInvoicePreview}>
            <Stack direction="row" gap="s" alignItems="center">
              <EyeIcon size={vars.iconSize.s} />
              {/* TODO: Add ContentId.PreviewInvoice */}
              Preview Invoice
            </Stack>
          </Button>
          <Button
            type="button"
            onClick={onCompleteAsUnpaid}
            disabled={disabled}
            variant="outline"
          >
            <Content id={ContentId.CompleteAsUnpaid} />
          </Button>
          <Button type="button" onClick={onPayAndComplete} disabled={disabled}>
            <Content id={ContentId.PayAndComplete} />
          </Button>
        </Stack>
      </Stack>
    );
  }

  if (step === 'invoicePreview') {
    return (
      <Stack direction="row" gap="m" justifyContent="spaceBetween" width="full">
        <Button type="button" variant="outline" onClick={goToCheckout}>
          {/* TODO: Add ContentId.BackToCheckout */}
          Back to Checkout
        </Button>
        <Button
          type="button"
          onClick={handleDownloadInvoice}
          disabled={isDownloading}
        >
          {/* TODO: Add ContentId.Download */}
          Download
        </Button>
      </Stack>
    );
  }

  return null;
};
